import { ArrowUpDownLongIcon } from "@solomon-labs/design/components/icons";
import { ButtonIcon, Text } from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React from "react";

import { easings } from "../../utils/easings";

interface RateRowProps {
    /** Conversion rate text, e.g. "1 USDC = 1 USDv". */
    rate: string;
    /** Called when the toggle/swap arrow is clicked. */
    onToggle?: () => void;
    /** Custom icon element. Defaults to the UpDownIcon wrapped in a raised chip. */
    icon?: React.ReactNode;
    /** Optional className. */
    className?: string;
}

/**
 * @version 1.4.0
 * @published 2026-04-25
 * @history
 *   1.4.0 — Removed the `tone` prop added in 1.3.0. Neither call site ever
 *           passed it, so the `"error"` badge was unreachable; the rate badge
 *           is unconditionally the default treatment.
 *   1.3.0 — Added `tone` prop. `"error"` recolors the rate badge with the
 *           semantic error palette so the row can carry an inline quote-
 *           unavailable signal without a new layout slot. The toggle icon
 *           stays unchanged so the swap-direction control remains visually
 *           identical across states.
 *   1.2.0 — Default icon now rendered inside a raised chip matching the rate
 *           text's visual treatment. Uses the UpDownIcon (short) instead of the
 *           `⇅` character.
 *   1.1.0 — Added optional `icon` prop for custom toggle icons.
 *   1.0.0 — Initial publish. Conversion rate indicator with toggle arrow.
 *           Used between send/receive input fields in swap forms.
 */
export function RateRow({
    rate,
    onToggle,
    icon,
    className
}: RateRowProps): React.ReactElement {
    const [rotation, setRotation] = React.useState(0);
    const shouldReduceMotion = useReducedMotion();
    const badgeClass =
        "text-foreground-base bg-surface-raised px-2 py-1 md:py-1.5";
    // When no toggle handler is supplied the button is non-interactive
    // — disable it so the visual matches (greyed surface, cursor not-
    // allowed) instead of a clickable-looking control that does
    // nothing. Used by buy-only surfaces (e.g. disconnected USDv
    // landing) where flipping Sell ↔ Buy isn't supported.
    const toggleDisabled = !onToggle;
    const handleToggle = (): void => {
        setRotation((current) => current + 180);
        onToggle?.();
    };

    return (
        <div
            className={`flex items-center justify-between bg-surface-ground h-8 ${className ?? ""}`}
        >
            <ButtonIcon
                variant="inverse-raised"
                size="md"
                icon={
                    <motion.span
                        className="inline-flex"
                        animate={{
                            rotate: shouldReduceMotion ? 0 : rotation,
                            scale: 1
                        }}
                        whileTap={
                            shouldReduceMotion ? undefined : { scale: 0.94 }
                        }
                        transition={{
                            rotate: {
                                duration: 0.2,
                                ease: easings.outQuint
                            },
                            scale: {
                                duration: 0.14,
                                ease: easings.outQuint
                            }
                        }}
                    >
                        {icon ?? <ArrowUpDownLongIcon />}
                    </motion.span>
                }
                onClick={onToggle ? handleToggle : undefined}
                disabled={toggleDisabled}
                className="px-1 md:px-1.5 py-1.5 md:[&_svg]:size-6"
            />
            <Text variant="small" render={<span />} className={badgeClass}>
                {rate}
            </Text>
        </div>
    );
}
